import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { toast } from "sonner";
import { SearchInput } from "@/components/ui/search-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { clientesAPI, Cliente } from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";
import {
  coerceCondicionIvaForTipos,
  condicionIvaOpcionesParaTipos,
  encodeTipoFacturaPermitida,
  normalizeStoredTiposPermitidosForForm,
  type TipoFacturaFlag,
} from "@/lib/clienteTipoFactura";
import {
  MSG_CUIT_FACTURA_B,
  cuitLengthError,
  defaultDocumentoBTipo,
  dniLengthError,
  isCuitComplete,
  isDniComplete,
  shouldWarnCuitOnFacturaB,
} from "@/lib/clienteDocumento";
import { mensajeBajaBloqueada } from "@/lib/bajaCopy";

type DocumentoBTipo = "cuit" | "dni";

// CUIT formatting helper: formats to XX-XXXXXXXX-X
const formatCUIT = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 10) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  }
};

// DNI: limit to 8 digits only
const formatDNI = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 8);
};

interface ClientesABMProps {
  /** Modal create-from-venta/presupuesto: solo alta, sin búsqueda/edición/borrado. */
  createOnly?: boolean;
  onClienteSaved?: (cliente: Cliente) => void;
}

export function ClientesABM({ createOnly = false, onClienteSaved }: ClientesABMProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [_loading, setLoading] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [isModified, setIsModified] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCuitBWarning, setShowCuitBWarning] = useState(false);
  const [dniError, setDniError] = useState("");
  const [cuitError, setCuitError] = useState("");
  const [docCheckPending, setDocCheckPending] = useState(false);
  const docCheckSeq = useRef(0);

  const [formData, setFormData] = useState({
    codigo: "",
    nombre: "",
    cuit: "",
    dni: "",
    domicilio: "",
    localidad: "",
    provincia: "",
    personaContacto: "",
    telefono: "",
    celular: "",
    whatsapp: "",
    email: "",
    saldo: 0,
    tipoFacturaPermitida: [] as TipoFacturaFlag[],
    condicionIva: "",
  });
  const [documentoBTipo, setDocumentoBTipo] = useState<DocumentoBTipo>("dni");

  const [showForm, setShowForm] = useState(createOnly);

  // Server-side search with debounce
  const [searchLoading, setSearchLoading] = useState(false);

  const searchClientes = async (query: string) => {
    if (!query.trim()) {
      setClientes([]);
      return;
    }
    try {
      setSearchLoading(true);  // Use separate loading state for search
      // Use server-side search for better performance
      const data = await clientesAPI.list({ search: query, page_size: 50 });
      setClientes(data);
    } catch (error) {
      console.error("Error searching clientes:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search effect (skip in createOnly — no list UI)
  useEffect(() => {
    if (createOnly) return;
    const timer = setTimeout(() => {
      searchClientes(searchQuery);
    }, 300); // 300ms debounce
    return () => clearTimeout(timer);
  }, [searchQuery, createOnly]);

  // filteredClientes is now just the server results
  const filteredClientes = clientes;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);

    // If we have a selected client and the search query no longer matches it,
    // reset the form to allow creating a new client
    if (selectedCliente && value.toLowerCase() !== selectedCliente.nombre.toLowerCase()) {
      resetForm();
      setSearchQuery(value); // Restore the search query after reset
    }

    setIsModified(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const cliente = clientes.find(c => c.nombre.toLowerCase() === searchQuery.trim().toLowerCase());
      if (cliente) {
        selectCliente(cliente);
      } else {
        startNewCliente();
      }
      setShowSuggestions(false);
    } else if (e.key === 'Tab' && showSuggestions && filteredClientes.length > 0) {
      e.preventDefault();
      selectCliente(filteredClientes[0]);
    }
  };

  const selectCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setShowForm(true);
    setSearchQuery(cliente.nombre);
    setDniError("");
    setCuitError("");
    // Ensure tipoFacturaPermitida is always an array
    const celularUnificado =
      (cliente.celular && String(cliente.celular).trim()) ||
      (cliente.whatsapp && String(cliente.whatsapp).trim()) ||
      "";
    const tipos = normalizeStoredTiposPermitidosForForm(
      cliente.tipoFacturaPermitida as string | TipoFacturaFlag[]
    );
    const cuitVal = cliente.cuit || "";
    const dniVal = cliente.dni || "";
    const rawIva =
      (cliente as Cliente).condicionIva || (cliente as Cliente).condicion_iva || "";
    const nextDocB: DocumentoBTipo = defaultDocumentoBTipo(tipos, rawIva);
    setDocumentoBTipo(nextDocB);
    setFormData({
      ...cliente,
      dni: dniVal,
      cuit: cuitVal,
      condicionIva: tipos.includes("A")
        ? coerceCondicionIvaForTipos(tipos, rawIva)
        : rawIva,
      tipoFacturaPermitida: tipos,
      celular: celularUnificado,
      whatsapp: celularUnificado,
    } as typeof formData);
    const nextShowCuit =
      tipos.includes("A") ||
      (tipos.includes("B") && !tipos.includes("A") && nextDocB === "cuit");
    const nextShowDni =
      tipos.includes("B") && !tipos.includes("A") && nextDocB === "dni";
    setDniError(nextShowDni ? dniLengthError(dniVal) : "");
    setCuitError(nextShowCuit ? cuitLengthError(cuitVal) : "");
    setShowSuggestions(false);
    setIsModified(false);
  };

  const startNewCliente = () => {
    setSelectedCliente(null);
    setShowForm(true);
    setDniError("");
    setCuitError("");
    setDocumentoBTipo("dni");
    setFormData({
      codigo: "",
      nombre: "",
      cuit: "",
      dni: "",
      domicilio: "",
      localidad: "",
      provincia: "",
      personaContacto: "",
      telefono: "",
      celular: "",
      whatsapp: "",
      email: "",
      saldo: 0,
      tipoFacturaPermitida: ["B", "X"], // Default: B + X enabled
      condicionIva: "Consumidor Final",
    });
    setIsModified(false);
  };

  // Auto-open empty create form when embedded as modal
  useEffect(() => {
    if (createOnly) {
      startNewCliente();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / createOnly only
  }, [createOnly]);

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData({ ...formData, [field]: value });
    setIsModified(true);
  };

  /**
   * Unicidad contra BE (solo vivos). Compara el valor como se persiste (trim).
   * Edit: excludeCodigo = self. Devuelve mensaje de error o "".
   */
  const checkDocumentoUnico = async (
    tipo: "cuit" | "dni",
    valor: string
  ): Promise<string> => {
    const trimmed = (valor || "").trim();
    const setError = tipo === "cuit" ? setCuitError : setDniError;
    if (!trimmed) {
      setError("");
      return "";
    }
    const lengthMsg =
      tipo === "cuit" ? cuitLengthError(trimmed) : dniLengthError(trimmed);
    if (lengthMsg) {
      setError(lengthMsg);
      return lengthMsg;
    }

    const seq = ++docCheckSeq.current;
    setDocCheckPending(true);
    try {
      const result = await clientesAPI.checkDocumento({
        tipo,
        valor: trimmed,
        excludeCodigo: selectedCliente?.codigo || null,
      });
      if (seq !== docCheckSeq.current) return "";
      if (!result.available) {
        const msg =
          result.message ||
          (tipo === "cuit"
            ? "Este CUIT ya pertenece a otro cliente."
            : "Este DNI ya pertenece a otro cliente.");
        setError(msg);
        return msg;
      }
      setError("");
      return "";
    } catch (err) {
      console.error("Error checking documento:", err);
      if (seq !== docCheckSeq.current) return "";
      // No bloquear por fallo de red en blur; Guardar reintenta / BE hard-block
      return "";
    } finally {
      if (seq === docCheckSeq.current) {
        setDocCheckPending(false);
      }
    }
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    // Validate that at least one tipo de factura is selected
    if (formData.tipoFacturaPermitida.length === 0) {
      toast.error("Debe seleccionar al menos un tipo de factura permitida");
      return;
    }

    // Validate that appropriate ID field is filled based on factura type
    const saveHasA = formData.tipoFacturaPermitida.includes("A");
    const saveHasB = formData.tipoFacturaPermitida.includes("B");
    const saveShowCuit = saveHasA || (saveHasB && !saveHasA && documentoBTipo === "cuit");
    const saveShowDni = saveHasB && !saveHasA && documentoBTipo === "dni";

    if (saveHasA && !formData.cuit.trim()) {
      toast.error("CUIT es requerido para Factura A");
      return;
    }

    if (saveHasB && !saveHasA && documentoBTipo === "cuit" && !formData.cuit.trim()) {
      toast.error("CUIT o DNI es requerido para Factura B");
      return;
    }

    if (saveHasB && !saveHasA && documentoBTipo === "dni" && !formData.dni.trim()) {
      toast.error("CUIT o DNI es requerido para Factura B");
      return;
    }

    if (saveShowCuit && formData.cuit.trim()) {
      const lengthMsg = cuitLengthError(formData.cuit);
      if (lengthMsg) {
        setCuitError(lengthMsg);
        toast.error(lengthMsg);
        return;
      }
    }
    if (saveShowDni && formData.dni.trim()) {
      const lengthMsg = dniLengthError(formData.dni);
      if (lengthMsg) {
        setDniError(lengthMsg);
        toast.error(lengthMsg);
        return;
      }
    }

    const ivaAllowed = condicionIvaOpcionesParaTipos(formData.tipoFacturaPermitida);
    if (
      saveHasB &&
      !saveHasA &&
      !ivaAllowed.some((o) => o.value === formData.condicionIva)
    ) {
      toast.error("Condición IVA: Consumidor Final o IVA Exento");
      return;
    }

    // Hard block: re-check documento activo (create + edit) contra vivos en BE
    if (saveShowCuit && formData.cuit.trim()) {
      const errMsg = await checkDocumentoUnico("cuit", formData.cuit);
      if (errMsg) {
        toast.error(errMsg);
        return;
      }
    }
    if (saveShowDni && formData.dni.trim()) {
      const errMsg = await checkDocumentoUnico("dni", formData.dni);
      if (errMsg) {
        toast.error(errMsg);
        return;
      }
    }

    try {
      setLoading(true);
      const celularVal = (formData.celular || "").trim();
      const condicionIva = coerceCondicionIvaForTipos(
        formData.tipoFacturaPermitida,
        formData.condicionIva,
      );
      // A → CUIT. B → CUIT o DNI según radio.
      const payload = {
        ...formData,
        condicionIva,
        cuit: saveShowCuit ? ((formData.cuit || "").trim() || null) : null,
        dni: saveShowDni ? ((formData.dni || "").trim() || null) : null,
        celular: celularVal || null,
        whatsapp: celularVal || null,
        tipoFacturaPermitida: encodeTipoFacturaPermitida(formData.tipoFacturaPermitida),
        activo: true,
      };

      if (selectedCliente) {
        if (!selectedCliente.codigo) throw new Error("Cliente Codigo missing");
        const updated = await clientesAPI.update(selectedCliente.codigo, {
          ...payload,
          activo: selectedCliente.activo
        });
        toast.success("Cliente actualizado exitosamente");
        dataRefresh.emit("clientes");
        onClienteSaved?.(updated);
      } else {
        const created = await clientesAPI.create(payload);
        toast.success("Cliente creado exitosamente");
        dataRefresh.emit("clientes");
        onClienteSaved?.(created);
        if (createOnly) {
          return;
        }
      }

      resetForm();
      // Refresh search results
      if (!createOnly) {
        searchClientes(searchQuery);
      }
    } catch (error: unknown) {
      console.error("Error saving cliente:", error);
      let msg = "Error al guardar cliente";
      if (error && typeof error === "object") {
        const o = error as Record<string, unknown>;
        if (typeof o.message === "string" && o.message.trim()) msg = o.message;
        const det = o.details;
        if (det && typeof det === "object" && !Array.isArray(det)) {
          const details = det as Record<string, unknown>;
          for (const key of ["cuit", "dni", "condicionIva", "condicion_iva"] as const) {
            const raw = details[key];
            if (Array.isArray(raw) && raw.length > 0) {
              msg = String(raw[0]);
              if (key === "cuit") setCuitError(msg);
              else if (key === "dni") setDniError(msg);
              break;
            }
            if (typeof raw === "string" && raw.trim()) {
              msg = raw;
              if (key === "cuit") setCuitError(msg);
              else if (key === "dni") setDniError(msg);
              break;
            }
          }
        }
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async () => {
    if (selectedCliente && selectedCliente.codigo) {
      setShowDeleteDialog(false);
      try {
        setLoading(true);
        await clientesAPI.delete(selectedCliente.codigo);
        toast.success("Cliente eliminado exitosamente");
        dataRefresh.emit("clientes");
        resetForm();
        searchClientes(searchQuery);
      } catch (error) {
        console.error("Error deleting cliente:", error);
        toast.error(mensajeBajaBloqueada(error, "Error al eliminar cliente"));
      } finally {
        setLoading(false);
      }
    }
  };

    const handleTipoFacturaChange = (tipo: "A" | "B" | "X", checked: boolean) => {
    let newTypes = [...formData.tipoFacturaPermitida];
    const wasBOnly =
      formData.tipoFacturaPermitida.includes("B") &&
      !formData.tipoFacturaPermitida.includes("A");

    if (checked) {
      // If enabling A or B, disable the other (they are mutually exclusive)
      if (tipo === "A" && newTypes.includes("B")) {
        newTypes = newTypes.filter(t => t !== "B");
      } else if (tipo === "B" && newTypes.includes("A")) {
        newTypes = newTypes.filter(t => t !== "A");
      }

      if (!newTypes.includes(tipo)) newTypes.push(tipo);
    } else {
      // When unchecking, remove the type
      newTypes = newTypes.filter(t => t !== tipo);

      // If unchecking A or B and X is the only one left, also remove X
      // because X requires either A or B to be enabled
      if ((tipo === "A" || tipo === "B") && newTypes.length === 1 && newTypes[0] === "X") {
        newTypes = [];
      }
    }

    // Solo validar el documento del toggle activo: limpiar error del campo oculto
    const nextShowCuit = newTypes.includes("A");
    const nextShowB = newTypes.includes("B") && !newTypes.includes("A");
    if (!nextShowCuit && !nextShowB) setCuitError("");
    if (!nextShowB) setDniError("");

    const nextIva = coerceCondicionIvaForTipos(newTypes, formData.condicionIva);
    // Cambiar *a* B (o quedar B + Consumidor Final) fuerza radio DNI.
    if (nextShowB && (!wasBOnly || nextIva === "Consumidor Final")) {
      setDocumentoBTipo("dni");
      setCuitError("");
    }

    setFormData({
      ...formData,
      tipoFacturaPermitida: newTypes,
      condicionIva: nextIva,
    });
    setIsModified(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setSearchQuery("");
    setSelectedCliente(null);
    setDniError("");
    setCuitError("");
    setDocumentoBTipo("dni");
    setFormData({
      codigo: "",
      nombre: "",
      cuit: "",
      dni: "",
      domicilio: "",
      localidad: "",
      provincia: "",
      personaContacto: "",
      telefono: "",
      celular: "",
      whatsapp: "",
      email: "",
      saldo: 0,
      tipoFacturaPermitida: ["B", "X"], // Default: B + X enabled
      condicionIva: "Consumidor Final",
    });
    setIsModified(false);
  };

  const handleDNIChange = (value: string) => {
    const formatted = formatDNI(value);
    handleInputChange("dni", formatted);
    if (!formatted.trim()) {
      setDniError("");
      return;
    }
    // Check al completar (7–8 dígitos); blur cubre el resto
    if (isDniComplete(formatted)) {
      void checkDocumentoUnico("dni", formatted);
    } else {
      setDniError("");
    }
  };

  const applyDocumentoBCuit = () => {
    setDocumentoBTipo("cuit");
    setDniError("");
    setIsModified(true);
  };

  const handleCUITChange = (value: string) => {
    const formatted = formatCUIT(value);
    handleInputChange("cuit", formatted);
    if (!formatted.trim()) {
      setCuitError("");
      return;
    }
    // Check al completar (11 dígitos); blur cubre el resto
    if (isCuitComplete(formatted)) {
      void checkDocumentoUnico("cuit", formatted);
    } else {
      setCuitError("");
    }
  };

  // Determine which ID field to show based on factura type
  const hasFacturaA = formData.tipoFacturaPermitida.includes("A");
  const hasFacturaB = formData.tipoFacturaPermitida.includes("B");
  const showCuitField = hasFacturaA || (hasFacturaB && !hasFacturaA && documentoBTipo === "cuit");
  const showDniField = hasFacturaB && !hasFacturaA && documentoBTipo === "dni";
  const dniIncompleto = showDniField && !!dniLengthError(formData.dni);
  const cuitIncompleto = showCuitField && !!cuitLengthError(formData.cuit);
  const ivaOpciones = condicionIvaOpcionesParaTipos(formData.tipoFacturaPermitida);

  return (
    <div className="space-y-4">
      {!createOnly && (
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <SearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Buscar cliente por nombre o código..."
              tabIndex={1}
            />
            {showSuggestions && searchQuery.trim() && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-input rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchLoading ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                    Buscando...
                  </div>
                ) : filteredClientes.length > 0 ? (
                  filteredClientes.map((cliente) => (
                    <button
                      key={cliente.codigo}
                      type="button"
                      onClick={() => selectCliente(cliente)}
                      className="w-full px-4 py-2 text-left hover:bg-muted text-sm flex justify-between items-center"
                    >
                      <span>{cliente.nombre}</span>
                      <span className="text-xs text-muted-foreground">{cliente.codigo}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                    No se encontraron clientes. Presione Enter para crear uno nuevo.
                  </div>
                )}
              </div>
            )}
          </div>
          <Button
            onClick={startNewCliente}
            variant="outline"
            className="flex items-center gap-1"
          >
            <span className="text-sm">Nuevo Cliente</span>
          </Button>
        </div>
      )}

      {showForm && (
        <div className={`space-y-4 ${createOnly ? "" : "border border-input rounded-lg p-4"}`}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Código</label>
              <input
                type="text"
                value={formData.codigo}
                disabled
                tabIndex={-1}
                className="w-full px-3 py-2 border border-input bg-muted rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nombre o Razón Social</label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => handleInputChange("nombre", e.target.value)}
                tabIndex={2}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>

            {/* Documento: A = CUIT. B = radio CUIT o DNI. Toggles A/B/X no se tocan. */}
            {hasFacturaB && !hasFacturaA && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Documento <span className="text-xs text-muted-foreground">(CUIT o DNI para Factura B)</span>
                </label>
                <div className="flex gap-6 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="documentoB"
                      checked={documentoBTipo === "cuit"}
                      onChange={() => {
                        if (
                          shouldWarnCuitOnFacturaB(
                            formData.tipoFacturaPermitida,
                            "cuit"
                          )
                        ) {
                          setShowCuitBWarning(true);
                          return;
                        }
                        applyDocumentoBCuit();
                      }}
                    />
                    CUIT
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="documentoB"
                      checked={documentoBTipo === "dni"}
                      onChange={() => {
                        setDocumentoBTipo("dni");
                        setCuitError("");
                        setIsModified(true);
                      }}
                    />
                    DNI
                  </label>
                </div>
              </div>
            )}

            {showCuitField && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  CUIT{" "}
                  <span className="text-xs text-muted-foreground">
                    {hasFacturaA ? "(para Factura A)" : "(para Factura B)"}
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.cuit}
                  onChange={(e) => handleCUITChange(e.target.value)}
                  onBlur={(e) => {
                    void checkDocumentoUnico("cuit", e.target.value);
                  }}
                  placeholder="XX-XXXXXXXX-X"
                  tabIndex={3}
                  className={`w-full px-3 py-2 border ${cuitError ? 'border-red-500' : 'border-input'} bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm`}
                />
                {cuitError && <p className="text-xs text-red-600 mt-1">{cuitError}</p>}
              </div>
            )}

            {showDniField && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  DNI <span className="text-xs text-muted-foreground">(para Factura B)</span>
                </label>
                <input
                  type="text"
                  value={formData.dni}
                  onChange={(e) => handleDNIChange(e.target.value)}
                  onBlur={(e) => {
                    void checkDocumentoUnico("dni", e.target.value);
                  }}
                  placeholder="XXXXXXXX"
                  tabIndex={3}
                  className={`w-full px-3 py-2 border ${dniError ? 'border-red-500' : 'border-input'} bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm`}
                />
                {dniError && <p className="text-xs text-red-600 mt-1">{dniError}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Domicilio</label>
              <input
                type="text"
                value={formData.domicilio}
                onChange={(e) => handleInputChange("domicilio", e.target.value)}
                tabIndex={4}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Localidad</label>
              <input
                type="text"
                value={formData.localidad}
                onChange={(e) => handleInputChange("localidad", e.target.value)}
                tabIndex={5}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Provincia</label>
              <input
                type="text"
                value={formData.provincia}
                onChange={(e) => handleInputChange("provincia", e.target.value)}
                tabIndex={6}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Tipos de Factura Permitidos
                <span className="block text-xs text-muted-foreground mt-1">
                  A y B son mutuamente excluyentes. X requiere A o B habilitado.
                </span>
              </label>
              <div className="flex gap-4">
                {["A", "B", "X"].map((tipo, index) => {
                  // X is always checked and disabled (can't be toggled)
                  const isX = tipo === "X";
                  const isChecked = isX || formData.tipoFacturaPermitida.includes(tipo as any);
                  const isDisabled = isX; // X is always disabled

                  return (
                    <label
                      key={tipo}
                      className={`flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={(e) => handleTipoFacturaChange(tipo as any, e.target.checked)}
                          tabIndex={7 + index}
                        />
                        <div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500"></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-300">
                        {tipo}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">
                Condición IVA (texto en facturas / NC PDF)
              </label>
              <select
                value={formData.condicionIva || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange("condicionIva", value);
                  if (
                    value === "Consumidor Final" &&
                    hasFacturaB &&
                    !hasFacturaA
                  ) {
                    setDocumentoBTipo("dni");
                    setCuitError("");
                  }
                }}
                disabled={hasFacturaA}
                className={`w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm ${hasFacturaA ? "bg-muted" : "bg-background"}`}
              >
                {!hasFacturaA && <option value="">Seleccionar…</option>}
                {ivaOpciones.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {hasFacturaA
                  ? "Factura A: Responsable Inscripto (automático, no se modifica)."
                  : "Factura B: Consumidor Final (habitual) o IVA Exento."}{" "}
                La plantilla pdfme usa el campo{" "}
                <code className="text-xs">cliente_condicion_iva</code>.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Persona de Contacto</label>
              <input
                type="text"
                value={formData.personaContacto}
                onChange={(e) => handleInputChange("personaContacto", e.target.value)}
                tabIndex={10}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <input
                type="text"
                value={formData.telefono}
                onChange={(e) => handleInputChange("telefono", e.target.value)}
                tabIndex={11}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Celular</label>
              <input
                type="text"
                value={formData.celular}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({ ...formData, celular: v, whatsapp: v });
                  setIsModified(true);
                }}
                placeholder="Incluye WhatsApp si aplica"
                tabIndex={12}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Un solo contacto móvil; se guarda en celular y WhatsApp del registro para compatibilidad.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">E-mail</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                tabIndex={14}
                className="w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Saldo</label>
              <input
                type="text"
                value={`$${(formData.saldo || 0).toLocaleString('es-AR')}`}
                disabled
                tabIndex={-1}
                className={`w-full px-3 py-2 border border-input bg-muted rounded-lg text-sm font-medium ${(formData.saldo || 0) < 0 ? 'text-red-600' : 'text-green-600'
                  }`}
              />
            </div>
          </div>

          <FormFooter
            onDelete={
              !createOnly && selectedCliente
                ? () => setShowDeleteDialog(true)
                : undefined
            }
            deleteTabIndex={16}
            onSave={handleSave}
            saveDisabled={
              !isModified ||
              !!dniError ||
              !!cuitError ||
              dniIncompleto ||
              cuitIncompleto ||
              docCheckPending ||
              formData.tipoFacturaPermitida.length === 0
            }
            saveTabIndex={15}
          />
        </div>
      )}

      <AlertDialog open={showCuitBWarning} onOpenChange={setShowCuitBWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>CUIT en cliente Factura B</AlertDialogTitle>
            <AlertDialogDescription>{MSG_CUIT_FACTURA_B}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver a DNI</AlertDialogCancel>
            <AlertDialogAction onClick={applyDocumentoBCuit}>
              Continuar con CUIT
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Está seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se dará de baja el cliente "{selectedCliente?.nombre}".
              No se puede eliminar si tiene facturas, recibos, presupuestos o saldo.
              Si la baja procede, el DNI/CUIT queda disponible para un cliente nuevo
              y se conservan código e identificador.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}